import type {
  CrashAnalysis,
  CrashAnalysisWindow,
  CrashAnalysisWindowKey,
  CrashCandle,
  CrashCandleColor,
  CrashColorCounts,
  CrashColorShare,
  CrashDistributionBucket,
  CrashDistributionSlice,
  CrashGameInfo,
  CrashPreGame,
  CrashRangeStat,
  CrashStreak,
  CrashVsTypical,
  CrashWindowColors,
} from '~/types/crash-pre-game';
import {
  CRASH_ANALYSIS_WINDOWS,
  CRASH_DISTRIBUTION_BUCKETS,
} from '~/types/crash-pre-game';

// Normalização da resposta pública de /crash/pre-game. Não há tradução de
// caixa a fazer (o core já manda camelCase) — o trabalho aqui é garantir que a
// tela receba SEMPRE o shape completo, mesmo que um campo falte, venha string
// ou venha null. Um `cycleHistory` ausente tem que virar `[]`, não explodir no
// `v-for` no meio de um tick de polling.

const CANDLE_COLORS: CrashCandleColor[] = ['blue', 'purple', 'pink'];

// `null` quando não dá pra virar número. Serve pros campos do analysis, onde
// null tem significado próprio ("sem dado suficiente") e não pode virar 0.
function toNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : null;
}

function toCount(value: unknown): number {
  const parsed = toNullableNumber(value);

  // Contagem é sempre número: ausência de dado e zero se renderizam igual
  // ("nenhuma vela dessa cor nos últimos 30 min").
  return parsed === null || parsed < 0 ? 0 : Math.trunc(parsed);
}

function mapGame(raw: any): CrashGameInfo {
  return {
    name: raw?.name ?? '',
    provider: raw?.provider ?? '',
    gameName: raw?.gameName ?? '',
  };
}

function mapColorCounts(raw: any): CrashColorCounts {
  return {
    blue: toCount(raw?.blue),
    purple: toCount(raw?.purple),
    pink: toCount(raw?.pink),
  };
}

// Vela sem multiplicador utilizável ou com cor desconhecida é DESCARTADA em vez
// de ganhar um default: a barra prefere ter uma vela a menos a pintar um
// quadrado cinza que o jogador leria como resultado real.
function mapCandle(raw: any): CrashCandle | null {
  const multiplier = toNullableNumber(raw?.multiplier);

  if (multiplier === null) return null;

  const color = raw?.color as CrashCandleColor;

  if (!CANDLE_COLORS.includes(color)) {
    // Cor fora das três conhecidas descarta a vela — e se o core mudar a régua
    // (ou a caixa da string), descarta TODAS, e a tela passa a dizer "aguardando
    // as primeiras velas" pra um jogo rodando. O aviso em dev é o que separa
    // "jogo novo sem histórico" de "contrato mudou".
    if (import.meta.dev) {
      console.warn(
        `[crash-pre-game] vela descartada: cor desconhecida ${JSON.stringify(
          raw?.color,
        )}`,
      );
    }

    return null;
  }

  return {
    multiplier,
    roundTime: typeof raw?.roundTime === 'string' ? raw.roundTime : '',
    color,
  };
}

function mapColorShare(raw: any): CrashColorShare {
  return {
    count: toCount(raw?.count),
    // `pct` vem pronto do core e NÃO é recalculado a partir do count: quem tem
    // a amostra é quem arredonda. Recalcular aqui daria um número diferente do
    // que o core publica em qualquer outro lugar.
    pct: toCount(raw?.pct),
  };
}

function mapWindowColors(raw: any): CrashWindowColors {
  return {
    blue: mapColorShare(raw?.blue),
    purple: mapColorShare(raw?.purple),
    pink: mapColorShare(raw?.pink),
  };
}

// A distribuição sai na ORDEM DAS FAIXAS conhecidas, não na ordem que o core
// mandou: a barra tem que ir de "<2x" a "50x+" sempre, senão uma resposta com a
// ordem trocada desenharia o gráfico ao contrário. Faixa que o core não mandou
// entra com 0 (é uma faixa sem ocorrência, e a barra vazia diz isso).
function mapDistribution(raw: any): CrashDistributionSlice[] {
  const byBucket = new Map<string, number>();

  if (Array.isArray(raw)) {
    for (const slice of raw) {
      const bucket = slice?.bucket;

      if (typeof bucket === 'string') {
        byBucket.set(bucket, toCount(slice?.count));
      }
    }
  }

  return CRASH_DISTRIBUTION_BUCKETS.map((bucket: CrashDistributionBucket) => ({
    bucket,
    count: byBucket.get(bucket) ?? 0,
  }));
}

function mapVsTypical(raw: any): CrashVsTypical | null {
  const actual = toNullableNumber(raw?.actual);
  const typical = toNullableNumber(raw?.typical);

  // A comparação só existe com os DOIS lados: "9 rosas" sem o típico não
  // compara nada, e o típico sozinho não diz o que aconteceu.
  if (actual === null || typical === null) return null;

  return { actual, typical };
}

function mapWindow(raw: any): CrashAnalysisWindow | null {
  if (!raw || typeof raw !== 'object') return null;

  return {
    total: toCount(raw.total),
    colors: mapWindowColors(raw.colors),
    distribution: mapDistribution(raw.distribution),
    biggest: toNullableNumber(raw.biggest),
    avgMultiplier: toNullableNumber(raw.avgMultiplier),
    vsTypical: {
      pink: mapVsTypical(raw.vsTypical?.pink),
    },
  };
}

function mapRange(raw: any): CrashRangeStat | null {
  const range = toNullableNumber(raw?.range);

  // Sem faixa o item não tem rótulo ("velas de ?x ou mais") — não há o que
  // mostrar.
  if (range === null) return null;

  return {
    range,
    perRounds: toNullableNumber(raw?.perRounds),
    lastRoundsAgo: toNullableNumber(raw?.lastRoundsAgo),
    lastSecondsAgo: toNullableNumber(raw?.lastSecondsAgo),
  };
}

function mapStreak(raw: any): CrashStreak | null {
  const color = raw?.color as CrashCandleColor;
  const length = toNullableNumber(raw?.length);

  // Cor fora das três conhecidas não tem estilo na tela — vira "sem sequência"
  // em vez de um chip sem cor.
  if (!CANDLE_COLORS.includes(color) || length === null) return null;

  return { color, length: Math.trunc(length) };
}

// `analysis` null (ou ausente) é o gate de compliance do core fechado
// (CRASH_ANALYSIS_ENABLED): o bloco inteiro fica fora da tela. Por isso null
// aqui é PRESERVADO, e não normalizado pra um objeto vazio — um objeto vazio
// renderizaria um card com título e nada dentro.
function mapAnalysis(raw: any): CrashAnalysis | null {
  if (!raw || typeof raw !== 'object') return null;

  const windows: CrashAnalysis['windows'] = {};

  for (const key of CRASH_ANALYSIS_WINDOWS) {
    const window = mapWindow(raw.windows?.[key]);

    // Janela ausente fica AUSENTE: a aba some. Um objeto zerado no lugar seria
    // lido como "nenhuma vela nesses 5 minutos", que é outra coisa.
    if (window) windows[key as CrashAnalysisWindowKey] = window;
  }

  const ranges = Array.isArray(raw.ranges)
    ? raw.ranges
        .map(mapRange)
        .filter((item: CrashRangeStat | null): item is CrashRangeStat =>
          Boolean(item),
        )
    : [];

  return {
    windows,
    ranges,
    streaks: {
      current: mapStreak(raw.streaks?.current),
      record: mapStreak(raw.streaks?.record),
    },
  };
}

export function mapCrashPreGame(raw: any): CrashPreGame {
  const cycleHistory = Array.isArray(raw?.cycleHistory)
    ? // A ordem (mais recente → mais antiga) vem pronta do core e NÃO é
      // reordenada aqui: a tela mostra o que o core considera "última vela".
      raw.cycleHistory
        .map(mapCandle)
        .filter((candle: CrashCandle | null): candle is CrashCandle =>
          Boolean(candle),
        )
    : [];

  return {
    game: mapGame(raw?.game),
    paidToday: toNullableNumber(raw?.paidToday),
    last30min: mapColorCounts(raw?.last30min),
    cycleHistory,
    analysis: mapAnalysis(raw?.analysis),
    updatedAt: typeof raw?.updatedAt === 'string' ? raw.updatedAt : null,
  };
}
